import { SentenceFilterConfig } from "@/components/SentenceFilter";
import api from "@/lib/api";
import { patientFilterFields } from "@/config/patientFilterMeta";
import {
  allowedKeysFromMeta,
  buildDateAnchors,
  buildFilterJsonSchema,
  renderDateAnchors,
  renderOutputRules,
} from "@/lib/llm";

// Patient filter result interface (matching the existing one from SentencePatientFilter)
export interface PatientFilterResult {
  // Patient Demographics
  patient_name?: string;
  patient_identifier?: string; // MRN / external id or FHIR id
  patient_phone?: string;
  patient_gender?: string;
  patient_age_min?: number;
  patient_age_max?: number;
  include_tags?: string;
  exclude_tags?: string;

  // Call Status & History
  call_statuses?: string;
  call_types?: string;
  call_date_start?: string;
  call_date_end?: string;
  has_calls?: boolean;

  // Call Outcome Analysis
  latest_call_status?: string;
  call_attempts_min?: number;
  call_attempts_max?: number;
  never_answered?: boolean;
  call_outcome_keys?: string;
  call_outcome_values?: string;

  // Consent Management
  consent_status?: string;
  consent_method?: string;
  needs_consent?: boolean;

  // Time-based Filters
  last_contact_start?: string;
  last_contact_end?: string;
  due_for_contact?: boolean;
  overdue_contact?: boolean;

  // Care Management
  care_plan_status?: string;
  has_active_care_plan?: boolean;
  scheduled_calls_pending?: boolean;

  // Complex Logical Filters
  call_success_rate_min?: number;
  call_success_rate_max?: number;
  response_pattern?: string;
  priority_level?: string;

  // Schedule-based Filters
  scheduled_after?: string;
  scheduled_before?: string;
  scheduled_today?: boolean;
  scheduled_tomorrow?: boolean;
  scheduled_this_week?: boolean;

  // Negative filtering for calls
  no_calls_start?: string;
  no_calls_end?: string;
  no_call_types?: string;
  no_call_types_start?: string;
  no_call_types_end?: string;
}

export interface PatientQueryResults {
  results: any[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  query_summary: {
    applied_filters: Record<string, any>;
    patient_count: number;
    avg_success_rate: number;
    consent_pending_count: number;
    overdue_count: number;
  };
}

/**
 * API keys the patient V3 endpoint accepts but the filter builder does not
 * surface as its own field.
 */
const PATIENT_EXTRA_KEYS = [
  "call_outcome_keys",
  "call_outcome_values",
  "scheduled_after",
  "scheduled_before",
  "no_calls_start",
  "no_calls_end",
  "no_call_types",
  "no_call_types_start",
  "no_call_types_end",
];

export const patientFilterAllowedKeys = allowedKeysFromMeta(
  patientFilterFields,
  PATIENT_EXTRA_KEYS
);

const patientFilterJsonSchema = buildFilterJsonSchema(
  patientFilterFields,
  patientFilterAllowedKeys,
  {
    call_success_rate_min: "number",
    call_success_rate_max: "number",
    patient_age_min: "number",
    patient_age_max: "number",
  }
);

export function buildPatientSystemPrompt(now: Date = new Date()): string {
  const anchors = buildDateAnchors(now);

  return `You are a filter compiler for a healthcare care-management app (APCM — Advanced Primary Care Management). You convert a care team member's natural language request about PATIENTS into a JSON object of filter parameters.

Available filter parameters:
- patient_name: string (fuzzy search across first/last/preferred names)
- patient_identifier: string (MRN / medical record number / external id / EHR id / FHIR id; case-insensitive substring match. Use this, NOT patient_name, whenever the user gives an MRN, chart number, record number, external id or FHIR id, or a bare alphanumeric token containing digits that is not a phone number)
- patient_phone: string
- patient_gender: string (M/F)
- patient_age_min: number
- patient_age_max: number  
- include_tags: string (comma-separated: include patients with ANY of these tags)
- exclude_tags: string (comma-separated: exclude patients with ANY of these tags)
- call_statuses: string (comma-separated: pending,completed,failed,voicemail)
- call_types: string (comma-separated: consent,check_in)
- call_date_start: string (ISO datetime)
- call_date_end: string (ISO datetime)
- has_calls: boolean
- latest_call_status: string (comma-separated)
- call_attempts_min: number
- call_attempts_max: number
- never_answered: boolean
- call_outcome_keys: string (comma-separated call outcome keys to filter by)
- call_outcome_values: string (JSON string of key-value pairs to filter call outcomes)
- consent_status: string (comma-separated: CONSENTED,DECLINED,UNCERTAIN,NOT_ASKED,none — "none" means no consent record at all)
- consent_method: string (comma-separated: phone,in_person,online)
- needs_consent: boolean
- last_contact_start: string (ISO datetime)
- last_contact_end: string (ISO datetime)
- due_for_contact: boolean
- overdue_contact: boolean
- care_plan_status: string (comma-separated: "all", "active", "approved", "pending", "declined" ... "all" if all care plans should be included)
- has_active_care_plan: boolean
- scheduled_calls_pending: boolean
- call_success_rate_min: number (0.0-1.0)
- call_success_rate_max: number (0.0-1.0)
- response_pattern: string (consistent,intermittent,non_responsive)
- priority_level: string (high,medium,low)
- scheduled_after: string (ISO datetime)
- scheduled_before: string (ISO datetime)
- scheduled_today: boolean
- scheduled_tomorrow: boolean
- scheduled_this_week: boolean
- no_calls_start: string (ISO datetime - exclude patients with ANY calls after this date)
- no_calls_end: string (ISO datetime - exclude patients with ANY calls before this date)
- no_call_types: string (comma-separated: exclude patients with these call types)
- no_call_types_start: string (ISO datetime - start date for excluding specific call types)
- no_call_types_end: string (ISO datetime - end date for excluding specific call types)

${renderDateAnchors(anchors)}

${renderOutputRules(patientFilterAllowedKeys)}

## Domain notes

- "consented" patients means consent_status = CONSENTED.
- A "check-in" is call_types / no_call_types value check_in; a "consent call" is consent.
- "Went to voicemail" for the most recent call is latest_call_status = voicemail; across all calls it is call_statuses = voicemail.
- "Haven't been called / not reached since <date>" is the negative no_calls_start / no_calls_end pair, not call_date_start.

## Examples

"Patients who need consent" -> {"needs_consent": true}
"MRN 12345" -> {"patient_identifier": "12345"}
"patient with fhir id abc123" -> {"patient_identifier": "abc123"}
"chart number A1B2C3" -> {"patient_identifier": "A1B2C3"}
"Female patients over 65" -> {"patient_gender": "F", "patient_age_min": 65}
"Patients who haven't answered calls" -> {"never_answered": true}
"High priority patients overdue for contact" -> {"priority_level": "high", "overdue_contact": true}
"Patients with calls scheduled today" -> {"scheduled_today": true}
"Patients with calls scheduled tomorrow" -> {"scheduled_tomorrow": true}
"Patients with calls scheduled this week" -> {"scheduled_this_week": true}
"Patients with no calls in the last 30 days" -> {"no_calls_start": "${anchors.days30Ago}", "no_calls_end": "${anchors.today}"}
"Patients with no consent calls this month" -> {"no_call_types": "consent", "no_call_types_start": "${anchors.monthStart}", "no_call_types_end": "${anchors.today}"}
"Patients who haven't been called at all" -> {"has_calls": false}
"Patients with any care plan" -> {"care_plan_status": "all"}
"Patients with active care plans" -> {"care_plan_status": "active"}
"Patients with approved or pending care plans" -> {"care_plan_status": "approved,pending"}
"consented patients with no check-in this month" -> {"consent_status": "CONSENTED", "no_call_types": "check_in", "no_call_types_start": "${anchors.monthStart}", "no_call_types_end": "${anchors.today}"}
"patients we called yesterday that went to voicemail" -> {"call_date_start": "${anchors.yesterday}", "call_date_end": "${anchors.yesterday}", "call_statuses": "voicemail"}
"consented patients over 70 who never answered" -> {"consent_status": "CONSENTED", "patient_age_min": 70, "never_answered": true}
"patients with approved care plans that are due for contact" -> {"care_plan_status": "approved", "due_for_contact": true}
"who haven't we reached in the last 90 days" -> {"no_calls_start": "${anchors.days90Ago}", "no_calls_end": "${anchors.today}"}
"patients with a call scheduled this week" -> {"scheduled_this_week": true}
"patients tagged diabetes but not hospice" -> {"include_tags": "diabetes", "exclude_tags": "hospice"}
"how many patients do we have" -> {}`;
}

export const patientFilterConfig: SentenceFilterConfig<
  PatientFilterResult,
  PatientQueryResults
> = {
  entityName: "Patient",
  buildSystemPrompt: () => buildPatientSystemPrompt(),
  allowedKeys: patientFilterAllowedKeys,
  jsonSchema: patientFilterJsonSchema,
  placeholder:
    "Describe the patients you want to find... (e.g., 'Patients who need consent calls', 'Female patients over 65 who haven't been contacted', 'High priority patients with failed calls')",
  cacheKey: "patient_filter_searches",
  fetchResults: async (
    organizationId: string,
    filters: PatientFilterResult
  ) => {
    return await api.getPatientsAdvancedV3(organizationId, {
      ...filters,
      page: 1,
      page_size: 20,
    });
  },
};
